import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Footer from '../components/PageFooter';
import { clientIntegrationService } from '../services/clientIntegrationService';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const EbayOAuthCallback: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const submitOnce = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state') ?? undefined;
    const oauthError = params.get('error');
    const oauthErrorDesc = params.get('error_description');

    if (oauthError) {
      setStatus('error');
      setErrorMessage(
        oauthErrorDesc || oauthError || 'eBay authorization was not completed.'
      );
      return;
    }

    if (!code) {
      setStatus('error');
      setErrorMessage('Missing authorization code. Return to API Management and try connecting eBay again.');
      return;
    }

    if (submitOnce.current) return;
    submitOnce.current = true;

    const send = async () => {
      setStatus('submitting');
      try {
        await clientIntegrationService.postEbayOAuthCode({ code, state });
        setStatus('success');
      } catch (err) {
        setStatus('error');
        let msg = '';
        if (err && typeof err === 'object' && err !== null && 'response' in err) {
          const response = err['response'];
          if (response && typeof response === 'object' && response !== null && 'data' in response) {
            const data = response['data'];
            if (data && typeof data === 'object' && data !== null && 'message' in data) {
              const m = data['message'];
              if (typeof m === 'string') msg = m;
            }
          }
        }
        if (!msg && err instanceof Error && err.message) msg = err.message;
        if (!msg) msg = 'Failed to connect eBay account.';
        setErrorMessage(msg);
      }
    };

    void send();
  }, [location.search]);

  useEffect(() => {
    if (status !== 'success') return;
    const t = window.setTimeout(() => {
      history.push('/api-management');
    }, 5000);
    return () => window.clearTimeout(t);
  }, [status, history]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <PageHeader />
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full card p-8 text-center">
          {status === 'submitting' || status === 'idle' ? (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
              <p className="text-gray-800 dark:text-gray-200">
                Confirming your eBay connection…
              </p>
            </>
          ) : null}

          {status === 'success' ? (
            <>
              <div className="text-green-600 dark:text-green-400 text-4xl mb-4">✓</div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                eBay account connected
              </h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                Your sign-in was confirmed. You will be redirected to API Management in a few seconds.
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500">
                Redirecting in 5 seconds…
              </p>
            </>
          ) : null}

          {status === 'error' && errorMessage ? (
            <>
              <div className="text-red-600 dark:text-red-400 text-4xl mb-4">!</div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Could not complete eBay connection
              </h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">{errorMessage}</p>
              <button
                type="button"
                onClick={() => history.push('/api-management')}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
              >
                Back to API Management
              </button>
            </>
          ) : null}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default EbayOAuthCallback;
